import React, { FC, useEffect } from 'react'
import { AppProps } from 'next/app'
import { QueryClientProvider } from 'react-query'
import { ThemeProvider } from '@libs/theme'
import '@libs/localization'
// Antd's full LESS, loaded once here instead of per-component (see babel.config.js style:false):
// guarantees every antd default is present from the very first compile, in both trees, so
// overrides never race a component's CSS arriving in a later-discovered chunk.
import 'antd/dist/antd.less'
import PageLayout from 'modules/common/PageLayout'
import GlobalModals from 'modules/common/GlobalModals'
import queryClient from 'query.client'
import { TaskUploaderProvider } from 'modules/tasks/TaskUploader/context'
import dayjs from 'dayjs'
import dayjsTimezone from 'dayjs/plugin/timezone'
import { useRouter } from 'next/router'
import { AntdConfigProvider } from '@libs/components'
import { NewOrderContextProvider } from 'modules/orders/context'
import ErrorBoundary from 'modules/common/ErrorBoundary'
import * as Sentry from '@sentry/react'
import Head from 'next/head'
import dynamic from 'next/dynamic'
import { isEfktRoute } from 'v2/routes.map'
import { FeatureFlagProvider, useNewUiEnabled } from 'feature.flag'

const EfktApp = dynamic(() => import('v2/App'), {
	ssr: false,
})

// Add timezone plugin to dayjs
dayjs.extend(dayjsTimezone)

const AppInner: FC<AppProps> = ({ Component, pageProps }) => {
	return (
		<>
			<Head>
				<link rel="shortcut icon" href="/fav-icon-32.png" />
			</Head>
			<ThemeProvider>
				<Sentry.ErrorBoundary fallback={ErrorBoundary} showDialog>
					<AntdConfigProvider>
						<TaskUploaderProvider>
							<GlobalModals.Provider>
								<PageLayout.Provider>
									<Component {...pageProps} />
								</PageLayout.Provider>
							</GlobalModals.Provider>
						</TaskUploaderProvider>
					</AntdConfigProvider>
				</Sentry.ErrorBoundary>
			</ThemeProvider>
		</>
	)
}

const LegacyApp: FC<AppProps> = props => {
	const router = useRouter()

	useEffect(() => {
		const onRouteChange = () => {
			const mfQueue: string[][] = (window as any)._mfq || []
			mfQueue.push(['newPageView', window.location.href.substr(window.location.origin.length)])
		}

		router.events.on('routeChangeComplete', onRouteChange)
		onRouteChange()

		return () => {
			router.events.off('routeChangeComplete', onRouteChange)
		}
	}, [])

	return (
		<NewOrderContextProvider>
			<AppInner {...props} />
		</NewOrderContextProvider>
	)
}

const App: FC<AppProps> = props => {
	const pathname = props.router.pathname
	const isRouteEfktEligible = isEfktRoute(pathname)
	const { newUiEnabled, isLoading } = useNewUiEnabled()

	// Blank only while a route that could actually render Efkt has no cached answer yet (a
	// browser's first-ever resolve for this branch) - rendering the full Legacy shell just to tear
	// it down a moment later is the double-blink this guards against. Every route ineligible for
	// Efkt renders immediately regardless, since the answer can never change what it shows.
	if (isRouteEfktEligible && isLoading) return null

	// `/presentation` has no signed-in visitor to carry a feature flag, so it decides its shell per
	// request instead of per user: `getServerSideProps` (`pages/presentation.tsx`, a rare permissioned
	// exception — see CLAUDE.md, "The pre-Efkt tree is read-only") resolves each case's owning backend
	// itself and tags the result `sourceBackend` in `pageProps`. Every other `EFKT_ROUTES` entry keeps
	// deciding purely from `newUiEnabled` below.
	const useEfktShell =
		pathname === '/presentation'
			? (props.pageProps as { sourceBackend?: string })?.sourceBackend === 'efkt'
			: isRouteEfktEligible && newUiEnabled

	return useEfktShell ? <EfktApp {...props} /> : <LegacyApp {...props} />
}

// ThemeProvider (@libs/theme) also mounts global styles, so it must not sit permanently above
// AppInner's own ThemeProvider - that would keep two competing global stylesheets mounted at
// once. It's only needed to theme this fallback itself, so it wraps just that.
const ThemedErrorFallback = (props: Parameters<typeof ErrorBoundary>[0]) => (
	<ThemeProvider>
		<ErrorBoundary {...props} />
	</ThemeProvider>
)

// Shared by both trees, and mounted above the shell decision itself. The QueryClientProvider is the
// load-bearing half: the migration-status request that decides the shell runs inside
// FeatureFlagProvider, before AppInner (and its own providers) has mounted. The ErrorBoundary is
// belt-and-braces now that nothing up here sets `useErrorBoundary` - a failed verdict resolves to the
// pre-Efkt tree rather than throwing - but it still catches anything the render of either shell
// throws before AppInner's own boundary exists. Deleted together with the flag/route-list branch at
// cutover.
const AppWithFeatureFlag: FC<AppProps> = props => (
	<QueryClientProvider client={queryClient}>
		<Sentry.ErrorBoundary fallback={ThemedErrorFallback} showDialog>
			<FeatureFlagProvider>
				<App {...props} />
			</FeatureFlagProvider>
		</Sentry.ErrorBoundary>
	</QueryClientProvider>
)

export default AppWithFeatureFlag
